import type { MessageDescriptor } from 'react-intl';
import type { Simplify } from 'type-fest';

/**
 * Represents the format of a classification mark.
 */
type MarkFormat = 'portion' | 'banner';

/**
 * Enum for classification mark types.
 */
enum MarkTypeEnum {
  DOMESTIC = 'DOMESTIC',
  JOINT = 'JOINT',
  FOREIGN = 'FOREIGN',
  NATO = 'NATO',
}

/**
 * Enum for parsing operators.
 */
enum ParsingOperatorsEnum {
  RD = 'RD',
  FRD = 'FRD',
  RD_N = 'RD-N',
  FRD_N = 'FRD-N',
  FRD_SG = 'FRD-SG',
  RD_SG = 'RD-SG',
  RD_N_SG = 'RD-N-SG',
  FRD_N_SG = 'FRD-N-SG',
  SAR = 'SAR',
  FOUO = 'FOUO',
  REL = 'REL',
  NF = 'NF',
  OC = 'OC',
  JOINT = 'JOINT',
  RELIDO = 'RELIDO',
  FISA = 'FISA',
  IMC = 'IMC',
  SI = 'SI',
  HCS = 'HCS',
  TK = 'TK',
  TK_G = 'TK-G',
  TK_GEOCAP = 'TK-GEOCAP',
  CUI = 'CUI',
  EXERCISE = 'E',
  XD = 'XD',
  ND = 'ND',
  DCNI = 'DCNI',
  SBU = 'SBU',
  SBU_NF = 'SBU-NF',
  PR = 'PR',
  FGI = 'FGI',
  WAIVED = 'WAIVED',
  ACCM = 'ACCM',
  HVSACO = 'HVSACO',
  BOHEMIA = 'BOHEMIA',
  ATOMAL = 'ATOMAL',
  DOUBLE_SLASH = '//',
  SINGLE_SLASH = '/',
  DASH = '-',
  DISPLAY_ONLY = 'DISPLAYONLY',
  REL_TO = 'REL TO',
  SG = 'SG',
}

/**
 * Represents a classification mark component.
 */
type MarkComponent = {
  classificationMarkKey?: keyof ClassificationMark;
  banner: string;
  portion: string;
};

/**
 * Enum for classification level values.
 * these are universal, and apply across all classification systems
 * {@link https://en.wikipedia.org/wiki/Classified_information#Table_of_equivalent_classification_markings_in_various_countries)}
 */
enum LevelValueEnum {
  TOP_SECRET = 3,
  SECRET = 2,
  CONFIDENTIAL = 1,
  RESTRICTED = 0.5,
  UNCLASSIFIED = 0,
}

/**
 * Represents a classification level.
 */
type Level = {
  value: LevelValueEnum;
  modifier?: '-A' | '-B';
} & MarkComponent;

/**
 * Enum for mark categories, which represent the primary groupings of controls in a classification mark.
 */
enum MarkCategoriesEnum {
  CLASSIFICATION_LEVEL = 'classification level',
  SCI = 'SCI control',
  SAP = 'SAP control',
  AEA = 'AEA control',
  FGI = 'FGI control',
  PRIMARY_DISSEM = 'primary dissemination control',
  SECONDARY_DISSEM = 'secondary dissemination control',
}

/**
 * Represents the order of mark categories.
 */
type MarkOrderArray = Array<{ control: string; category: MarkCategoriesEnum }>;

const markCategoryOrder = [
  MarkCategoriesEnum.CLASSIFICATION_LEVEL,
  MarkCategoriesEnum.SCI,
  MarkCategoriesEnum.SAP,
  MarkCategoriesEnum.AEA,
  MarkCategoriesEnum.FGI,
  MarkCategoriesEnum.PRIMARY_DISSEM,
  MarkCategoriesEnum.SECONDARY_DISSEM,
];

const triTetraFields = ['fgi', 'relTo', 'displayOnly'] as const;

/**
 * Represents the classification mark string array fields.
 */
const classificationMarkStringArrayFields = [
  ...triTetraFields,
  'accm',
  'sap',
  'sci',
  'sg',
  'otherDissem',
] as const;

type ClassificationMarkStringArrayFieldsType = {
  [K in (typeof classificationMarkStringArrayFields)[number]]?: Array<string>;
};

/**
 * Represents the classification mark boolean fields.
 */
const classificationMarkBooleanFields = [
  'atomal',
  'bohemia',
  'fouo',
  'cui',
  'exercise',
  'dcni',
  'oc',
  'imc',
  'nf',
  'pr',
  'fisa',
  'waived',
  'relido',
  'xd',
  'nd',
  'sbu',
  'hvsaco',
  'rd',
  'frd',
  'cnwdi',
] as const;

type ClassificationMarkBooleanFieldsType = {
  [K in (typeof classificationMarkBooleanFields)[number]]?: boolean;
};

type TriTetragraphField = (typeof triTetraFields)[number];

type ClassificationMarkStringArrayKey = keyof ClassificationMarkStringArrayFieldsType;

/**
 * Type definition for a classification mark
 *
 * @param classificationLevel a value from LevelValueEnum indicating the classification level (top secret, secret, etc.)
 * @param type a value from MarkTypeEnum indicating the type of the mark (domestic, joint, foreign, or NATO)
 * @param atomal US or UK RD or FRD that has been released to NATO
 * @param bohemia NATO TS information that has is SIGINT derived
 * @param sci sensitive compartmented information markings
 * @param sap special access program markings
 * @param fgi foreign government information (who owns the information)
 * @param relTo information releasable to the stated countries
 * @param displayOnly for the stated countries, this information is for display only (they are not given a copy for retention)
 * @param accm ACCM (alternative compensatory control measures)
 * @param fouo FOR OFFICIAL USE ONLY (used only for unclassified information)
 * @param cui CONTROLLED UNCLASSIFIED INFORMATION (used only for unclassified information)
 * @param exercise for use in unclassified exercises (used only for unclassified information)
 * @param dcni DOD UNCLASSIFIED CONTROLLED NUCLEAR INFORMATION
 * @param oc ORCON (originator controlled)
 * @param imc IMCON (imagery controlled)
 * @param nf NOFORN (no foreign release)
 * @param pr PROPIN (proprietary information)
 * @param fisa FISA (Foreign Intelligence Surveillance Act)
 * @param waived WAIVED (unacknowledged SAP in which access is extremely limited)
 * @param relido RELIDO (releasable by Information Disclosure Official)
 * @param xd EXDIS (exclusive distribution, not releasable to foreign governments/organizations)
 * @param nd NODIS (eyes only, to be read only by those listed in the distribution instructions; a step above EXDIS)
 * @param sbu SBU (sensitive but unclassified)
 * @param hvsaco HVSACO (handle via special access channels only)
 * @param rd RD (AEA mark for restricted data)
 * @param frd FRD (AEA mark for formerly restricted data)
 * @param sg SIGMA (RD/FRD categories that concern design/manufacture/utilization of atomic weapons)
 * @param cnwdi RD-N/FRD-N (AEA mark for critical nuclear weapons design information)
 * @param otherDissem catch all for other dissemination markings (TBD if we need this?)
 * @param warnings any warnings that were generated during parsing
 */
type ClassificationMark = Simplify<
  {
    classificationLevel?: LevelValueEnum;
    type?: MarkTypeEnum;
    warnings?: Array<ClassificationMarkError>;
  } & ClassificationMarkStringArrayFieldsType &
    ClassificationMarkBooleanFieldsType
>;

/**
 * Represents a classification mark error.
 */
type ClassificationMarkError = MessageDescriptor & { values?: { [key: string]: string } };

/**
 * Represents the type of a classification option in the autocomplete dropdown
 */
type ClassificationOption =
  | { mark: ClassificationMark; error: null }
  | { mark: null; error: ClassificationMarkError };

/**
 * Represents a token type.
 */
type TokenType = 'operator' | 'organization' | 'classification' | 'unknown';

/**
 * Represents a token.
 */
type Token = [TokenType, string];

const baseClassificationMark: ClassificationMark = {
  classificationLevel: LevelValueEnum.UNCLASSIFIED,
  type: MarkTypeEnum.DOMESTIC,
};

type ClassificationResult<T, E = ClassificationMarkError[]> =
  | {
      ok: true;
      val: T;
    }
  | {
      ok: false;
      val: E;
    };

function ClassificationOk<T>(val: T): ClassificationResult<T, never> {
  return { ok: true, val };
}

function ClassificationError<E>(val: E): ClassificationResult<never, E> {
  return { ok: false, val };
}

/**
 * Represents the classification display options for artifact embeds
 */
type EmbeddedClassificationDisplay = {
  classificationAlignment?: 'diagonal-negative' | 'centered' | 'diagonal-positive';
  classificationVisible?: boolean;
};

export type {
  ClassificationMark,
  Level,
  TokenType,
  Token,
  ClassificationOption,
  ClassificationMarkError,
  ClassificationResult,
  MarkComponent,
  MarkFormat,
  MarkOrderArray,
  ClassificationMarkStringArrayKey,
  TriTetragraphField,
  EmbeddedClassificationDisplay,
};

export {
  MarkCategoriesEnum,
  MarkTypeEnum,
  LevelValueEnum,
  ParsingOperatorsEnum,
  baseClassificationMark,
  classificationMarkStringArrayFields,
  classificationMarkBooleanFields,
  triTetraFields,
  ClassificationOk,
  ClassificationError,
  markCategoryOrder,
};
